import React, { useContext } from "react";
import LinkToContactUs from "components/LinkToContactUs";
import { Dropdown, Button, TextBox, TextArea, List } from "@sia/style-guide";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { formatTrusteeReview } from "./utils";
import GlobalContext from "contexts/GlobalContext";
import { useSurveyFormSession } from "./SurveyFormSessionContext";
import "./DataCollection.scss";

const translationKeyPrefix = "benchmarking.data-collection-page";

/* eslint-disable react/prop-types */
export function SurveyPageTop({ pageTitle, surveyYear, deadline, translate, lang }) {
  const {
    trusteeName,
    nogaList,
    cantonsList,
    officeSizeList,
    selectedNoga,
    selectedCanton,
    selectedOfficeSize,
    submitOptions,
    surveyConcluded,
    trusteeReview,
    surveySubmitted,
    clientReply,
    setClientReply,
    setIsSubmitModalOpen,
    OnReviewOrConcluded,
    isSurveyFinished,
    reopenSubmittedSurvey,
  } = useContext(DataCollectionContext);
  const { hasAnyUnsavedChanges, isCollectionDataSubmitted } = useSurveyFormSession();

  const onOpenSubmitModal = () => setIsSubmitModalOpen(true);
  const { survey } = useContext(GlobalContext);
  const isReviewed = surveySubmitted && !surveyConcluded && trusteeReview;
  const canReopenSubmittedSurvey = surveySubmitted && !surveyConcluded;
  const NogaCantonOfficeSizeDropdownSection = ({
    translate: translateText,
    nogaList: availableNoga,
    cantonsList: availableCantons,
    officeSizeList: availableOfficeSizes,
    selectedNoga: currentNoga,
    selectedCanton: currentCanton,
    selectedOfficeSize: currentOfficeSize,
  }) => (
    <>
      <Dropdown
        id="noga-dropdown"
        disabled={isSurveyFinished || OnReviewOrConcluded}
        label={translateText(`${translationKeyPrefix}.noga-dropdown.label`)}
        placeholder={translateText("benchmarking.dropdown.placeholder")}
        items={availableNoga}
        onChange={(event, option) => submitOptions("noga", option)}
        value={currentNoga}
        narrow
        invalidText={currentNoga?.id ? "" : translateText(`${translationKeyPrefix}.noga-dropdown.error.message`)}
      />
      <Dropdown
        id="canton-dropdown"
        disabled={isSurveyFinished || OnReviewOrConcluded}
        label={translateText(`${translationKeyPrefix}.canton-dropdown.label`)}
        placeholder={translateText("benchmarking.dropdown.placeholder")}
        items={availableCantons}
        onChange={(event, option) => submitOptions("canton", option)}
        value={currentCanton}
        narrow
        invalidText={currentCanton?.id ? "" : translateText(`${translationKeyPrefix}.canton-dropdown.error.message`)}
      />
      <Dropdown
        id="officeSize-dropdown"
        disabled={isSurveyFinished || OnReviewOrConcluded}
        label={translateText(`${translationKeyPrefix}.office-size.label`)}
        placeholder={translateText("benchmarking.dropdown.placeholder")}
        items={availableOfficeSizes}
        onChange={(event, option) => submitOptions("office-size", option)}
        value={currentOfficeSize}
        narrow
      />
    </>
  );

  const TrusteeReviewAndClientReplySection = ({ trusteeReview: review, clientReply: reply, setClientReply: setReply, translate: translateText }) => (
    <>
      <div className="text-input sia-margin-03-bottom">
        <div>
          <div className="sia-text-04 bx--label trustee-review-title">
            <div>{translateText("benchmarking.survey.trustee.review.title")}</div>
          </div>
          <pre className="sia-text-10 trustee-review">
            {formatTrusteeReview(review).map((line, index) => {
              if (Array.isArray(line)) {
                return <List title="" key={index} items={line.map((entry) => ({ content: entry }))} variant="UNORDERED" />;
              }
              if (line === "") return <br key={index} />;
              return <div key={index}>{line}</div>;
            })}
          </pre>
        </div>
      </div>
      <div className="text-input sia-margin-01-bottom client-reply-text-area">
        <div>
          <div className="bx--label">
            <div>{translateText("benchmarking.survey.client.reply.title")}</div>
          </div>
        </div>
      </div>
      <TextArea
        onChange={(event) => setReply(event.target.value)}
        value={reply}
        placeholder={translateText("benchmarking.survey.client.reply.description")}
      />
    </>
  );

  const SubmitSection = ({ translate: translateText, onOpenSubmit, language, dataComplete, hasUnsavedChanges }) => {
    const warningKey = !dataComplete
      ? "benchmarking.survey.submit.warning.incomplete-survey"
      : hasUnsavedChanges
      ? "benchmarking.survey.submit.warning.unsaved-changes"
      : null;

    const title = translateText("benchmarking.survey.submit.title");

    return (
      <div className={warningKey ? "submit-warning" : ""} role="alert">
        {warningKey ? (
          <>
            <div className="submit-info__icon" aria-hidden="true">
              i
            </div>
            <div className="submit-warning__content">
              <div className="submit-warning__title">{title}</div>
              <div className="submit-warning__text">{translateText(warningKey)}</div>
            </div>
          </>
        ) : (
          <TextBox
            items={[
              {
                paragraph_type: "text_box_title",
                title_color: { name: "SIAblack" },
                text_box_title: title,
              },
              {
                paragraph_type: "text_box_paragraph",
                title_color: { name: "SIAblack" },
                paragraph: translateText("benchmarking.survey.submit.question"),
              },
            ]}
            lang={language}
          />
        )}
        <div className="submit-button-container">
          <Button kind="primary" disabled={Boolean(warningKey)} onClick={() => onOpenSubmit(true)}>
            {translateText("benchmarking.survey.submit")}
          </Button>
        </div>
      </div>
    );
  };

  const ReopenSection = ({ translate: translateText, onReopen, language }) => (
    <div className="survey-reopen-box sia-margin-03-bottom">
      <TextBox
        items={[
          {
            paragraph_type: "text_box_title",
            title_color: { name: "SIAblack" },
            text_box_title: translateText("benchmarking.survey.reopen.paragraph"),
          },
        ]}
        lang={language}
      />
      <div className="submit-buttton-container">
        <Button kind="primary" onClick={onReopen}>
          {translateText("benchmarking.survey.reopen.button")}
        </Button>
      </div>
    </div>
  );

  const ConfirmationMessagesSection = ({ translate: translateText, finished, concluded, language, canReopen, onReopen }) => {
    if (finished) {
      if (concluded) {
        return (
          <TextBox
            items={[
              {
                paragraph_type: "text_box_title",
                title_color: { name: "SIAblack" },
                text_box_title: translateText("benchmarking.survey.confirmation.message.fourth-part", { year: survey.surveyYear }),
              },
            ]}
            lang={lang}
          />
        );
      }
      return null;
    }
    return (
      <>
        <TextBox
          items={[
            {
              paragraph_type: "text_box_title",
              title_color: { name: "SIAblack" },
              text_box_title: concluded
                ? translateText("benchmarking.survey.concluded.confirmation.message")
                : translateText("benchmarking.survey.confirmation.message.first-part"),
            },
          ]}
          lang={language}
        />
        <TextBox
          items={[
            {
              paragraph_type: "text_box_paragraph",
              title_color: { name: "SIAblack" },
              paragraph: concluded
                ? translateText("benchmarking.survey.confirmation.message.first-part")
                : translateText("benchmarking.survey.confirmation.message.second-part"),
            },
          ]}
          lang={language}
        />
        {canReopen && <ReopenSection translate={translateText} language={lang} onReopen={onReopen} />}
        <TextBox
          items={[
            {
              paragraph_type: "text_box_paragraph",
              title_color: { name: "SIAblack" },
              paragraph: translateText("benchmarking.survey.confirmation.message.third-part"),
            },
          ]}
          lang={language}
        />
        <TextBox
          items={[
            {
              paragraph_type: "text_box_title",
              title_color: { name: "SIAblack" },
              text_box_title: translateText("benchmarking.survey.confirmation.message.fourth-part"),
            },
          ]}
          lang={lang}
        />
      </>
    );
  };

  return (
    <>
      <h2>
        {pageTitle} {surveyYear}
      </h2>
      {isSurveyFinished ? (
        <>
          <p className="sia-text-04">{translate("benchmarking.landing-page.logged-in.paragraph.finished-survey")}</p>
          {surveyConcluded ? <p className="sia-text-10">{translate(`${translationKeyPrefix}.lead`)}</p> : null}
        </>
      ) : (
        <>
          <p className="sia-text-04">{translate(`${translationKeyPrefix}.cut-off.date`)}</p>
          <p className="sia-text-10">{translate(`${translationKeyPrefix}.lead`)}</p>
          <p className="sia-text-10">{translate(`${translationKeyPrefix}.sub-lead`, { name: trusteeName })}</p>
          <p className="sia-text-10">{translate(`${translationKeyPrefix}.sub-lead-2`)}</p>
          <p className="sia-text-04">{deadline}</p>
        </>
      )}
      <LinkToContactUs translate={translate} translationKeyPrefix={translationKeyPrefix} />
      <NogaCantonOfficeSizeDropdownSection
        translate={translate}
        nogaList={nogaList}
        cantonsList={cantonsList}
        officeSizeList={officeSizeList}
        selectedNoga={selectedNoga}
        selectedCanton={selectedCanton}
        selectedOfficeSize={selectedOfficeSize}
      />
      {isSurveyFinished || OnReviewOrConcluded ? (
        <ConfirmationMessagesSection
          translate={translate}
          language={lang}
          finished={isSurveyFinished}
          concluded={surveyConcluded}
          canReopen={canReopenSubmittedSurvey}
          onReopen={reopenSubmittedSurvey}
        />
      ) : (
        <>
          {isReviewed && (
            <TrusteeReviewAndClientReplySection
              translate={translate}
              trusteeReview={trusteeReview}
              clientReply={clientReply}
              setClientReply={setClientReply}
            />
          )}
          <SubmitSection
            translate={translate}
            onOpenSubmit={onOpenSubmitModal}
            language={lang}
            dataComplete={isCollectionDataSubmitted}
            hasUnsavedChanges={hasAnyUnsavedChanges}
          />
        </>
      )}
    </>
  );
}
